import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { type ConfigSummary, clearConfig, getConfig, saveConfig } from '../config';

// Re-export from FeaturesTab for backward compatibility
export { isPreprodParentWindowEventsEnabled } from './FeaturesTab';

interface ConfigurationTabProps {
    configSummary: ConfigSummary;
    onConfigChange: () => void;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ configSummary, onConfigChange }) => {
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);

    useEffect(() => {
        const config = getConfig();
        setApiKey(config.apiKey || '');
    }, []);

    const handleSave = () => {
        saveConfig({
            apiKey: apiKey.trim() || undefined,
        });
        onConfigChange();
    };

    const handleClear = () => {
        clearConfig();
        setApiKey('');
        onConfigChange();
    };

    return (
        <div className="m-0 h-full overflow-y-auto">
            {/* Configuration Status */}
            <Alert
                className={`mb-4 ${configSummary.hasApiKey ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}
            >
                {configSummary.hasApiKey ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                )}
                <AlertDescription>
                    <div className="text-sm font-medium mb-2">
                        Configuration Status: {configSummary.hasApiKey ? 'Complete' : 'Incomplete'}
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span>API Key:</span>
                            <span
                                className={
                                    configSummary.hasApiKey ? 'text-green-600' : 'text-red-600'
                                }
                            >
                                {configSummary.hasApiKey ? '✅ Configured' : '❌ Not set'}
                                <span className="text-gray-500 ml-1">
                                    ({configSummary.source.apiKey})
                                </span>
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Environment:</span>
                            <span className="font-medium">{configSummary.environment}</span>
                        </div>
                    </div>
                </AlertDescription>
            </Alert>

            <div className="mb-3">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: label is visually associated with the input below */}
                <label className="block mb-1 text-xs font-medium">🔑 API Key:</label>
                <div className="flex items-center gap-2">
                    <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Enter your API key"
                        className="flex-1 h-9"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="h-9 w-9"
                    >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                    Stored locally in your browser. Takes precedence over .env file.
                </div>
            </div>

            <div className="flex gap-2 justify-end mb-3">
                <Button
                    onClick={handleClear}
                    variant="outline"
                    size="sm"
                    className="border-red-500 text-red-500 hover:bg-red-50"
                >
                    Clear All
                </Button>
                <Button onClick={handleSave} size="sm">
                    Save Configuration
                </Button>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <h5 className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    💡 Configuration Info
                </h5>
                <div className="space-y-1 text-xs text-blue-600">
                    <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                        <span>API Key stored locally in browser</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                        <span>API Endpoint managed by endpoint selector above</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-300 rounded-full"></div>
                        <span>Use custom endpoint option for testing</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigurationTab;
